import React, { useState, useEffect, useRef } from 'react';
import { Group } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import dayjs from 'dayjs';
import type { CostFilterParams } from '@models/cost';
import './DateRangeFilter.scss';

interface DateRangeFilterProps {
    onFilterChange: (filters: CostFilterParams) => void;
    initialFilters?: CostFilterParams;
}

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({ onFilterChange, initialFilters }) => {
    const [startDate, setStartDate] = useState<Date | null>(
        initialFilters?.startDate
            ? dayjs(initialFilters.startDate).startOf('day').toDate()
            : dayjs().subtract(7, 'day').startOf('day').toDate()
    );

    const [endDate, setEndDate] = useState<Date | null>(
        initialFilters?.endDate
            ? dayjs(initialFilters.endDate).startOf('day').toDate()
            : dayjs().startOf('day').toDate()
    );

    const isInitialMount = useRef(true);
    const onFilterChangeRef = useRef(onFilterChange);
    onFilterChangeRef.current = onFilterChange;

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        const filters: CostFilterParams = {};

        if (startDate) {
            filters.startDate = dayjs(startDate).format('YYYY-MM-DD');
        }
        if (endDate) {
            filters.endDate = dayjs(endDate).format('YYYY-MM-DD');
        }

        onFilterChangeRef.current(filters);
    }, [startDate, endDate]);

    return (
        <Group gap="xs" align="center" wrap="wrap" className="date-range-filter">
            <DatePickerInput
                label="Start Date"
                value={startDate}
                onChange={setStartDate}
                size="sm"
                w={160}
            />
            <DatePickerInput
                label="End Date"
                value={endDate}
                onChange={setEndDate}
                minDate={startDate || undefined}
                size="sm"
                w={160}
            />
        </Group>
    );
};

export default DateRangeFilter;